import { useState } from 'react';
import { Send, CheckCircle, AlertTriangle, Mail, Clipboard, ClipboardCheck } from 'lucide-react';
import { sendCredentialsEmail } from '../../services/api';

export default function EmailTest() {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [userName, setUserName] = useState('John Smith');
  const [userEmail, setUserEmail] = useState('john.smith@company.com');
  const [tempPassword, setTempPassword] = useState('TempPass123!');
  const [forceChangePassword, setForceChangePassword] = useState(true);
  const [licenseName, setLicenseName] = useState('Microsoft 365 Business Basic');
  const [clientName, setClientName] = useState('Test Company Inc.');

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSendEmail = async () => {
    if (!recipientEmail) return;

    setSending(true);
    setResult(null);

    try {
      const response = await sendCredentialsEmail({
        recipientEmail,
        userName,
        userEmail,
        tempPassword,
        forceChangePassword,
        licenseName: licenseName || undefined,
        clientName,
      });
      setResult(response);
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : 'Failed to send email',
      });
    } finally {
      setSending(false);
    }
  };

  // Generate mailto link
  const getMailtoLink = () => {
    const subject = encodeURIComponent(`New Account Created - ${userName}`);
    const body = encodeURIComponent(`
New User Account Created
========================

Organization: ${clientName}

Account Details:
----------------
Name: ${userName}
Email/Username: ${userEmail}
Temporary Password: ${tempPassword}

${forceChangePassword ? 'Note: User will be required to change password on first sign-in.' : ''}

${licenseName ? `License Assigned: ${licenseName}` : 'License: None assigned'}

Sign-in URL: https://portal.office.com

Please share these credentials securely with the user.
    `.trim());
    return `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopy = async () => {
    const text = `
New User Account Created
========================

Organization: ${clientName}

Account Details:
----------------
Name: ${userName}
Email/Username: ${userEmail}
Temporary Password: ${tempPassword}

${forceChangePassword ? 'Note: User will be required to change password on first sign-in.' : ''}

${licenseName ? `License Assigned: ${licenseName}` : 'License: None assigned'}

Sign-in URL: https://portal.office.com

Please share these credentials securely with the user.
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Email Test</h1>
        <p className="text-muted-foreground mt-1">
          Test the credentials email sending functionality
        </p>
      </div>

      <div className="bg-card rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-medium text-foreground border-b border-border pb-2">
          Test Data (simulates user creation)
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              User Name
            </label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              User Email
            </label>
            <input
              type="text"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Temp Password
            </label>
            <input
              type="text"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Client Name
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            License Name (optional)
          </label>
          <input
            type="text"
            value={licenseName}
            onChange={(e) => setLicenseName(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="forceChange"
            checked={forceChangePassword}
            onChange={(e) => setForceChangePassword(e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="forceChange" className="ml-2 text-sm text-muted-foreground">
            Force password change on first sign-in
          </label>
        </div>

        <hr className="border-border my-4" />

        <h2 className="text-lg font-medium text-foreground">
          Test Email Sharing Options
        </h2>

        {/* Option 1: Copy to Clipboard */}
        <button
          type="button"
          onClick={handleCopy}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
            copied
              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
              : 'bg-card border-border text-muted-foreground hover:bg-accent'
          }`}
        >
          {copied ? (
            <ClipboardCheck className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Clipboard className="h-5 w-5 flex-shrink-0" />
          )}
          <div className="text-left">
            <div className="font-medium">{copied ? 'Copied!' : 'Copy to Clipboard'}</div>
            <div className="text-xs text-muted-foreground">Copy account details to paste manually</div>
          </div>
        </button>

        {/* Option 2: Open in Email App */}
        <a
          href={getMailtoLink()}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border bg-card border-border text-muted-foreground hover:bg-accent transition-all"
        >
          <Mail className="h-5 w-5 flex-shrink-0" />
          <div className="text-left">
            <div className="font-medium">Open in Email App</div>
            <div className="text-xs text-muted-foreground">Opens Outlook/Mail with pre-filled content</div>
          </div>
        </a>

        {/* Option 3: Send Email via SMTP */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Send className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
            <div className="text-left">
              <div className="font-medium text-muted-foreground">Send Email Directly</div>
              <div className="text-xs text-muted-foreground">Send credentials via SMTP server</div>
            </div>
          </div>

          <div className="px-4 py-3 space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Enter recipient email"
                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground placeholder-muted-foreground"
              />
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={!recipientEmail || sending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send
                  </>
                )}
              </button>
            </div>

            {result && (
              <div className={`p-3 rounded-lg ${
                result.success
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                <div className={`flex items-center gap-2 ${
                  result.success
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {result.success ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5" />
                  )}
                  <span className="font-medium">
                    {result.success ? result.message : result.error}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
